import React, { useEffect, useState, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MicrobiomeUpload } from "@/components/microbiome/MicrobiomeUpload";
import { MicrobiomeSummary } from "@/components/microbiome/MicrobiomeSummary";
import { OrganismBars } from "@/components/microbiome/OrganismBars";
import { SampleList } from "@/components/microbiome/SampleList";
import { MicrobiomeExtractModal } from "@/components/microbiome/MicrobiomeExtractModal";
import { MicrobiomeApothecary } from "@/components/microbiome/MicrobiomeApothecary";

export default function Microbiome() {
  const { user } = useAuth();
  const [samples, setSamples] = useState([]);
  const [latest, setLatest] = useState(null);
  const [recs, setRecs] = useState(null);
  const [credits, setCredits] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [modal, setModal] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const labRef = useRef(null);

  const load = async () => {
    const [s, l, c] = await Promise.all([
      api.get("/microbiome/samples"),
      api.get("/microbiome/latest"),
      api.get("/credits/balance").catch(() => ({ data: null })),
    ]);
    setSamples(s.data);
    const hasLatest = l.data && l.data.sample;
    setLatest(hasLatest ? l.data : null);
    if (c.data) setCredits(c.data);
    if (hasLatest) {
      api.get("/microbiome/apothecary")
        .then((r) => setRecs(r.data))
        .catch(() => setRecs(null));
    } else {
      setRecs(null);
    }
  };

  useEffect(() => { load(); }, []);

  const buyCredits = async () => {
    setBuyingCredits(true);
    try {
      const { data } = await api.post("/checkout/credits", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start credit checkout.");
      setBuyingCredits(false);
    }
  };

  const extractReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    const sizeMb = (file.size / 1_000_000).toFixed(1);
    const t = toast.loading(`Reading ${file.name} (${sizeMb} MB)…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/microbiome/extract", {
        method: "POST",
        body: fd,
      });
      let data = null;
      try { data = await r.json(); } catch (err) { console.debug("Non-JSON extract response", err); }
      toast.dismiss(t);
      if (!r.ok) {
        const detailMsg = data?.detail || "";
        const msg = typeof detailMsg === "string" ? detailMsg : (detailMsg?.message || "");
        if (r.status === 402 && /credit/i.test(msg)) {
          toast.error((tt) => (
            <span>Out of credits.{" "}
              <button onClick={() => { toast.dismiss(tt); buyCredits(); }} className="underline font-medium text-[#C05C42]" data-testid="microbiome-toast-buy">Buy 25 for $19</button>
            </span>
          ), { duration: 10000 });
        } else if (r.status === 402) {
          toast.error("Microbiome analysis is a Vital/Sovereign feature.");
        } else if (/budget|exceeded/i.test(msg)) {
          toast.error("AI credit exhausted. Top up your Universal Key, then try again.", { duration: 8000 });
        } else {
          toast.error(typeof detailMsg === "string" ? detailMsg : `Extraction failed (${r.status})`);
        }
      } else if (!data.organisms?.length) {
        toast.error("No organisms detected. Make sure this is a Bioscreen FMA report and the scan is clear.");
      } else {
        toast.success(`Extracted ${data.organisms.length} organisms from ${data.pages} page(s).`);
        if (data.credits) setCredits((c) => ({ ...(c || {}), ...data.credits }));
        setModal({
          organisms: data.organisms,
          selected: new Set(data.organisms.map((_, i) => i)),
          labName: data.lab_name || "Bioscreen FMA",
          labDate: data.report_date || "",
          diversityNote: data.diversity_note || null,
        });
      }
    } catch (err) {
      toast.dismiss(t);
      toast.error(`Extraction failed: ${err?.message || "network error"}`);
    } finally {
      setExtracting(false);
      if (labRef.current) labRef.current.value = "";
    }
  };

  const toggleRow = (idx) => setModal((m) => {
    if (!m) return m;
    const sel = new Set(m.selected);
    sel.has(idx) ? sel.delete(idx) : sel.add(idx);
    return { ...m, selected: sel };
  });

  const updateRow = (idx, field, val) => setModal((m) => {
    if (!m) return m;
    const organisms = m.organisms.map((o, i) => (i === idx ? { ...o, [field]: val } : o));
    return { ...m, organisms };
  });

  const confirmSave = async () => {
    if (!modal) return;
    const num = (v) => (v === "" || v === null || v === undefined || isNaN(parseFloat(v)) ? null : parseFloat(v));
    const chosen = modal.organisms
      .filter((_, i) => modal.selected.has(i))
      .map((o) => ({
        organism: o.organism,
        group: o.group || null,
        count: num(o.count),
        count_text: o.count_text || null,
        unit: o.unit || null,
        reference_low: num(o.reference_low),
        reference_high: num(o.reference_high),
        status: o.status || null,
        note: o.note || null,
      }))
      .filter((o) => o.organism && o.organism.trim());
    if (chosen.length === 0) {
      toast.error("Pick at least one organism");
      return;
    }
    try {
      await api.post("/microbiome/samples", {
        name: modal.labName || "Bioscreen FMA",
        date: modal.labDate || null,
        source: "ocr",
        diversity_note: modal.diversityNote,
        organisms: chosen,
      });
      toast.success(`Saved ${chosen.length} organisms.`);
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const toggle = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    try {
      const r = await api.get(`/microbiome/samples/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Could not load report");
    }
  };

  const remove = async (id) => {
    await api.delete(`/microbiome/samples/${id}`);
    if (expandedId === id) { setExpandedId(null); setDetail(null); }
    load();
  };

  const hasPaidPlan = ["vital", "sovereign"].includes(user?.plan);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-16 pb-10 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-3">Your inner garden</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B]">Gut Microbiome</h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl">
          Upload your Bioscreen FMA (Faecal Microbial Analysis) — the culture-based gold standard.
          We map your commensal terrain, surface what&apos;s out of range, and Massey reads it
          alongside your biomarkers and protocols.
        </p>
        {!hasPaidPlan && (
          <div className="mt-6 inline-block bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-4 text-sm text-[#5C6359]" data-testid="microbiome-locked-note">
            Microbiome analysis is included with Vital &amp; Sovereign memberships.
          </div>
        )}

        <MicrobiomeUpload
          extracting={extracting}
          labRef={labRef}
          extractReport={extractReport}
          credits={credits}
          buyCredits={buyCredits}
          buyingCredits={buyingCredits}
        />

        {latest && (
          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <MicrobiomeSummary summary={latest.summary} diversityNote={latest.sample?.diversity_note} date={latest.sample?.date} />
            <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-6">
              <p className="label-eyebrow mb-4">Latest terrain</p>
              <OrganismBars organisms={(latest.organisms || []).slice(0, 8)} />
            </div>
          </div>
        )}

        {latest && recs && (
          <MicrobiomeApothecary products={recs.products} basedOn={recs.based_on} memberId={user?.id} />
        )}
      </section>

      <SampleList samples={samples} expandedId={expandedId} detail={detail} toggle={toggle} remove={remove} />

      <MicrobiomeExtractModal modal={modal} setModal={setModal} toggleRow={toggleRow} updateRow={updateRow} confirmSave={confirmSave} />

      <Footer />
    </div>
  );
}
